import axios from 'axios';
import cookies from 'js-cookie';
import { message } from 'antd';
import get from 'lodash/get';
import * as queries from '~settings/queries';
import endpoints from '~settings/endpoints';
import i18n from '~settings/i18n';
import { cookieNames } from '~constants/storage';
import { formatSimpleParams, bookingExportData, orderExportData } from '~helpers';
import { fetch, updateCommunicationStatus, handleFetchCatch } from '~actions/shared';
import csvExporter from '~client/helpers/csvExporter';
import { reconciliationActionTypes as actions } from '~client/constants/actionTypes';
import { communicationStatus } from '~constants';
import { bookingJourneyType } from 'common/constants/booking';
import isEmpty from 'lodash/isEmpty';

export const dateFormat = 'YYYY-MM-DD';

export const setListLandlordOpportunities = payload => ({
  type: actions.SET_RECONCILIATION_LIST,
  payload,
});

export const setListLandlordOrders = payload => ({
  type: actions.SET_RECONCILIATION_LIST,
  payload,
  isOrder: true,
});

export const listLandlordReconciliationOpportunities = params => dispatch => {
  const newParams = formatSimpleParams(
    {
      pageNumber: 1,
      pageSize: 10,
      finalMoveInDateEnd: null,
      finalMoveInDateStart: null,
      finalMoveOutDateEnd: null,
      finalMoveOutDateStart: null,
      completedAtEnd: null,
      completedAtStart: null,
      landlordApplicationIds: [],
      landlordBookingStatuses: [],
      propertyIds: [],
      xbookingReferenceIds: [],
      invoiceNumber: null,
    },
    params,
  );
  fetch({
    endpoint: endpoints.graphql.url(),
    communicationType: actions.GET_RECONCILIATION_LIST_CS,
    params: queries.listLandlordReconciliationOpportunities(newParams),
    dispatch,
    successAction: setListLandlordOpportunities,
    onError: e => {
      let error_info = i18n.t('landlord.error.default');
      if (e && !isEmpty(e)) {
        const error = e[0];
        const code = error?.extensions?.code;
        const message = error?.message;
        if (code === 'CLASS_VALIDATION_ERROR' && message) {
          error_info = message;
        }
      }
      message.error(error_info);
    },
  });
};

export const listLandlordReconciliationOrders = params => dispatch => {
  const newParams = formatSimpleParams(
    {
      pageNumber: 1,
      pageSize: 10,
      receivableStatus: null,
      orderReferenceId: null,
      countrySlug: null,
      citySlug: null,
      bookingJourney: null,
      moveInDateEnd: null,
      moveInDateStart: null,
      moveOutDateEnd: null,
      moveOutDateStart: null,
      createdDateStart: null,
      createdDateEnd: null,
      completedDateStart: null,
      completedDateEnd: null,
      invoiceNumber: null,
      propertyIds: null,
      commissionTriggerPoint: null,
    },
    params,
  );

  fetch({
    endpoint: endpoints.graphql.url(),
    communicationType: actions.GET_RECONCILIATION_LIST_CS,
    params: queries.listLandlordReconciliationOrders(newParams),
    dispatch,
    successAction: setListLandlordOrders,
    onError: e => {
      let error_info = i18n.t('landlord.error.default');
      if (e && !isEmpty(e)) {
        const error = e[0];
        const code = error?.extensions?.code;
        const message = error?.message;
        if (code === 'CLASS_VALIDATION_ERROR' && message) {
          error_info = message;
        }
      }
      message.error(error_info);
    },
  });
};

export const getLandlordPropertyList =
  (onSuccess = () => {}, pageNumber = 1, pageSize = 10, keyword = '', bookingJourneys) =>
  dispatch => {
    fetch({
      endpoint: endpoints.graphql.url(),
      communicationType: actions.GET_LANDLORD_PROPERTY_CS,
      params: queries.getReconciliationLandlordProperties({
        pageNumber: pageNumber,
        pageSize,
        name: keyword,
        bookingJourneys,
      }),
      dispatch,
      onSuccess: res => {
        onSuccess(res);
      },
      onError: () => {
        message.error(i18n.t('landlord.error.default'));
      },
    });
  };

export const listLandlordReconciliationOpportunitiesCount =
  (params, onSuccess = () => {}) =>
  dispatch => {
    const newParams = formatSimpleParams(
      {
        finalMoveInDateEnd: null,
        finalMoveInDateStart: null,
        finalMoveOutDateEnd: null,
        finalMoveOutDateStart: null,
        completedAtEnd: null,
        completedAtStart: null,
        landlordApplicationIds: [],
        landlordBookingStatuses: [],
        propertyIds: [],
        xbookingReferenceIds: [],
        invoiceNumber: null,
      },
      params,
    );
    fetch({
      endpoint: endpoints.graphql.url(),
      communicationType: actions.GET_RECONCILIATION_LIST_COUNT_CS,
      params: queries.listLandlordReconciliationOpportunitiesCount(newParams),
      dispatch,
      onSuccess: res => {
        onSuccess(res);
      },
    });
  };

export const exportListLandlordOrders = params => async dispatch => {
  dispatch(
    updateCommunicationStatus({
      actionType: actions.EXPORT_LIST_CS,
      status: communicationStatus.FETCHING,
    }),
  );

  const pageSize = 200;
  let pageNumber = 1;
  let hasMoreData = true;

  while (hasMoreData) {
    params.pageNumber = pageNumber;
    params.pageSize = pageSize;

    const queryParams = formatSimpleParams(
      {
        pageNumber,
        pageSize,
        propertyIds: null,
        receivableStatus: null,
        orderReferenceId: null,
        countrySlug: null,
        citySlug: null,
        bookingJourney: null,
        moveInDateEnd: null,
        moveInDateStart: null,
        moveOutDateEnd: null,
        moveOutDateStart: null,
        createdDateStart: null,
        createdDateEnd: null,
        completedDateStart: null,
        completedDateEnd: null,
        invoiceNumber: null,
      },
      params,
    );

    const exportListParams = queries.listLandlordReconciliationOrders(queryParams);

    try {
      const response = await axios({
        method: 'post',
        url: endpoints.graphql.url(),
        data: exportListParams,
        headers: {
          Authorization: cookies.get(cookieNames.token)
            ? `Bearer ${cookies.get(cookieNames.token)}`
            : '',
          'Current-Role': 'landlord',
        },
      });

      if (
        typeof response.data === 'object' &&
        typeof response.data.error === 'undefined' &&
        typeof response.data.data.listDashboardOrderReceivables === 'object' &&
        response.data.data.listDashboardOrderReceivables.edges instanceof Array
      ) {
        const edges = get(response, 'data.data.listDashboardOrderReceivables.edges');
        const { headers, data } = orderExportData(edges, params.bookingJourney);
        hasMoreData = edges.length === pageSize;

        const title = {
          [bookingJourneyType.SEMI_AUTOMATIC]: 'Reconciliation - Book',
          [bookingJourneyType.INSTANT_BOOK]: 'Reconciliation - Connect(Student.Com)',
          [bookingJourneyType.LEAD_GENERATION]: 'Reconciliation - List',
        };

        const fileName =
          pageNumber === 1 && !hasMoreData
            ? `${title[params.bookingJourney]}`
            : `${title[params.bookingJourney]}_Page_${pageNumber}`;

        csvExporter(headers, data, fileName);

        pageNumber += 1;
      } else {
        hasMoreData = false;
        dispatch(
          updateCommunicationStatus({
            actionType: actions.EXPORT_LIST_CS,
            status: communicationStatus.ERROR,
            error: response.data,
          }),
        );
      }
    } catch (error) {
      handleFetchCatch({
        actionType: actions.EXPORT_LIST_CS,
        dispatch,
        error,
      });
      hasMoreData = false;
    }
  }

  dispatch(
    updateCommunicationStatus({
      actionType: actions.EXPORT_LIST_CS,
      status: communicationStatus.IDLE,
    }),
  );
};

export const exportListLandlordOpportunities = params => async dispatch => {
  dispatch(
    updateCommunicationStatus({
      actionType: actions.EXPORT_LIST_CS,
      status: communicationStatus.FETCHING,
    }),
  );

  const pageSize = 200;
  let pageNumber = 1;
  let hasMoreData = true;

  while (hasMoreData) {
    params.pageNumber = pageNumber;
    params.pageSize = pageSize;

    const queryParams = formatSimpleParams(
      {
        pageNumber,
        pageSize,
        finalMoveInDateEnd: null,
        finalMoveInDateStart: null,
        finalMoveOutDateEnd: null,
        finalMoveOutDateStart: null,
        completedAtEnd: null,
        completedAtStart: null,
        landlordApplicationIds: [],
        landlordBookingStatuses: [],
        propertyIds: [],
        invoiceNumber: null,
        xbookingReferenceIds: [],
      },
      params,
    );

    const exportListParams = queries.listLandlordReconciliationOpportunities(queryParams);

    try {
      const response = await axios({
        method: 'post',
        url: endpoints.graphql.url(),
        data: exportListParams,
        headers: {
          Authorization: cookies.get(cookieNames.token)
            ? `Bearer ${cookies.get(cookieNames.token)}`
            : '',
          'Current-Role': 'landlord',
        },
      });

      if (
        typeof response.data === 'object' &&
        typeof response.data.error === 'undefined' &&
        typeof response.data.data.listOpportunityReconciliation === 'object' &&
        response.data.data.listOpportunityReconciliation.edges instanceof Array
      ) {
        const edges = get(response, 'data.data.listOpportunityReconciliation.edges');
        const { headers, data } = bookingExportData(edges);
        hasMoreData = edges.length === pageSize;

        const fileName =
          pageNumber === 1 && !hasMoreData
            ? `Reconciliation - Recontracting`
            : `Reconciliation - Recontracting_Page_${pageNumber}`;

        csvExporter(headers, data, fileName);

        pageNumber += 1;
      } else {
        hasMoreData = false;
        dispatch(
          updateCommunicationStatus({
            actionType: actions.EXPORT_LIST_CS,
            status: communicationStatus.ERROR,
            error: response.data,
          }),
        );
      }
    } catch (error) {
      handleFetchCatch({
        actionType: actions.EXPORT_LIST_CS,
        dispatch,
        error,
      });
      hasMoreData = false;
    }
  }

  dispatch(
    updateCommunicationStatus({
      actionType: actions.EXPORT_LIST_CS,
      status: communicationStatus.IDLE,
    }),
  );
};

export const searchCountryList =
  (value, successCallback = () => {}) =>
  dispatch => {
    const query = queries.searchCountry({
      pageNumber: 1,
      pageSize: 100,
      query: value,
      // bookingJourneys: ['SEMI_AUTOMATIC', 'INSTANT_BOOK'],
    });
    fetch({
      dispatch,
      endpoint: endpoints.graphql.url(),
      params: query,
      communicationType: actions.COUNTRY_LIST_CS,
      onSuccess: res => {
        successCallback(res);
      },
    });
  };

export const searchCityList =
  (value, successCallback = () => {}) =>
  dispatch => {
    const query = queries.searchCity({
      pageNumber: 1,
      pageSize: 100,
      query: value,
      bookingJourneys: ['SEMI_AUTOMATIC', 'INSTANT_BOOK'],
    });
    fetch({
      dispatch,
      endpoint: endpoints.graphql.url(),
      params: query,
      communicationType: actions.CITY_LIST_CS,
      onSuccess: res => {
        successCallback(res);
      },
    });
  };
